'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { QrCode, Eye, Check, Save, ListChecks } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { formatCellValue } from '@/lib/build-dynamic-form-columns';
import { useRouter } from '@/i18n/routing';
import type { DynamicFormField } from '@/features/dynamic-forms';
import type { Registration } from '@/features/registration';

interface StickingCardProps {
  registration: Registration;
  dynamicFormFields?: DynamicFormField[];
  onViewQrData: (registration: Registration) => void;
  onOpenActions: (id: string) => void;
  onInlineSave?: (regId: number, startPage: number, endPage: number) => void;
  isSaving?: boolean;
}

const STAGE_STYLES: Record<string, string> = {
  sticking:
    'bg-amber-100 text-amber-800 hover:bg-amber-100 dark:bg-amber-900 dark:text-amber-200',
  review:
    'bg-purple-100 text-purple-800 hover:bg-purple-100 dark:bg-purple-900 dark:text-purple-200',
  completed:
    'bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900 dark:text-green-200'
};

const STAGE_STATUS_STYLES: Record<string, string> = {
  ready_for_numbering:
    'bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-200',
  ready_for_printing:
    'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200',
  returned_for_correction:
    'bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-200'
};

export function StickingCard({
  registration,
  dynamicFormFields,
  onViewQrData,
  onOpenActions,
  onInlineSave,
  isSaving
}: StickingCardProps) {
  const t = useTranslations('sticking');
  const tc = useTranslations('common');
  const router = useRouter();

  const hasBatch = !!registration.latest_batch_id;

  const [startPage, setStartPage] = useState(1);
  const [endPage, setEndPage] = useState(1);

  const stageLabels: Record<string, string> = {
    sticking: tc('stageSticking'),
    review: tc('stageReview'),
    completed: tc('stageCompleted')
  };

  const subStatus = registration.stage_status;
  const subStatusLabels: Record<string, string> = {
    ready_for_numbering: t('subStatusReadyForNumbering'),
    ready_for_printing: t('subStatusReadyForPrinting'),
    returned_for_correction: t('subStatusReturnedForCorrection')
  };

  const currentSheets = hasBatch
    ? (registration.latest_batch_end_page ?? 0) -
      (registration.latest_batch_start_page ?? 0) +
      1
    : null;

  const originalSheets =
    registration.latest_batch_original_end_page != null
      ? (registration.latest_batch_original_end_page ?? 0) -
        (registration.latest_batch_original_start_page ?? 0) +
        1
      : null;

  const sheetDiff =
    currentSheets != null && originalSheets != null
      ? currentSheets - originalSheets
      : 0;

  const inlineSheets = Math.max(0, endPage - startPage + 1);

  const responses = registration.form_responses as Record<
    string,
    unknown
  > | null;

  return (
    <Card className='border-border hover:border-primary/30 transition-colors'>
      <CardContent className='p-6'>
        {/* Top: container number + stage badge */}
        <div className='mb-3 flex items-start justify-between'>
          <button
            type='button'
            className='text-primary font-mono text-sm font-bold hover:underline'
            onClick={() =>
              router.push(
                `/dashboard/containers/${registration.id}?stage=sticking`
              )
            }
          >
            {registration.container_number}
          </button>
          <div className='flex flex-wrap items-center justify-end gap-1'>
            <Badge
              variant='secondary'
              className={cn(
                'shrink-0',
                STAGE_STYLES[registration.current_stage] ?? ''
              )}
            >
              {stageLabels[registration.current_stage] ??
                registration.current_stage}
            </Badge>
            {registration.current_stage === 'sticking' &&
              subStatus &&
              subStatusLabels[subStatus] && (
                <Badge
                  variant='secondary'
                  className={cn('shrink-0', STAGE_STATUS_STYLES[subStatus])}
                >
                  {subStatusLabels[subStatus]}
                </Badge>
              )}
          </div>
        </div>

        {/* Entity name */}
        {registration.entity_name_ar && (
          <p className='text-foreground mb-3 truncate text-sm font-medium'>
            {registration.entity_name_ar}
          </p>
        )}

        {/* Dynamic form fields */}
        {dynamicFormFields && dynamicFormFields.length > 0 && (
          <div className='text-muted-foreground mb-3 space-y-1 text-sm'>
            {dynamicFormFields.slice(0, 5).map((field) => (
              <div key={field.id} className='flex items-start gap-1.5'>
                <span className='shrink-0 font-medium'>{field.label}:</span>
                <span className='truncate'>
                  {formatCellValue(responses?.[String(field.id)], field.type)}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Batch info */}
        <div className='text-muted-foreground mb-4 space-y-1.5 text-sm'>
          {hasBatch ? (
            <>
              <div className='flex items-center justify-between'>
                <span className='font-medium'>{t('firstPage')}</span>
                <span>{registration.latest_batch_start_page}</span>
              </div>
              <div className='flex items-center justify-between'>
                <span className='font-medium'>{t('lastPageCol')}</span>
                <div className='flex items-center gap-1.5'>
                  <span>{registration.latest_batch_end_page}</span>
                  <Badge
                    variant='secondary'
                    className='bg-green-100 px-1.5 py-0 text-[10px] text-green-800 dark:bg-green-900 dark:text-green-200'
                  >
                    <Check className='me-0.5 h-2.5 w-2.5' />
                    {t('saved')}
                  </Badge>
                </div>
              </div>
              <div className='flex items-center justify-between'>
                <span className='font-medium'>{t('totalSheets')}</span>
                <div className='flex items-center gap-1.5'>
                  <span className='font-medium'>{currentSheets}</span>
                  {sheetDiff !== 0 && (
                    <Badge
                      variant='secondary'
                      className={cn(
                        'text-[10px] whitespace-nowrap',
                        sheetDiff > 0
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      )}
                    >
                      {sheetDiff > 0 ? '+' : ''}
                      {t('sheetDiff', { count: sheetDiff })}
                    </Badge>
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className='flex items-center justify-between'>
                <span className='font-medium'>{t('firstPage')}</span>
                <Input
                  type='number'
                  min={1}
                  value={startPage}
                  onChange={(e) => {
                    const num = Math.max(1, Number(e.target.value));
                    setStartPage(num);
                    if (endPage < num) setEndPage(num);
                  }}
                  className='h-7 w-16 text-center text-xs'
                />
              </div>
              <div className='flex items-center justify-between'>
                <span className='font-medium'>{t('lastPageCol')}</span>
                <Input
                  type='number'
                  min={startPage}
                  value={endPage}
                  onChange={(e) => {
                    const num = Math.max(startPage, Number(e.target.value));
                    setEndPage(num);
                  }}
                  className='h-7 w-16 text-center text-xs'
                />
              </div>
              <div className='flex items-center justify-between'>
                <span className='font-medium'>{t('totalSheets')}</span>
                {inlineSheets > 0 && (
                  <Badge
                    variant='secondary'
                    className='bg-blue-100 text-[10px] whitespace-nowrap text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                  >
                    {t('sheetDiff', { count: inlineSheets })}
                  </Badge>
                )}
              </div>
            </>
          )}
        </div>

        {/* Bottom: actions */}
        <div className='flex items-center justify-end gap-1 border-t pt-3'>
          {/*{registration.current_stage === 'sticking' && (
            <Button
              variant='ghost'
              size='icon'
              className='h-8 w-8'
              onClick={() => onOpenActions(String(registration.id))}
              title={t('stickingActions')}
            >
              <ListChecks className='h-4 w-4' />
            </Button>
          )}*/}
          {!hasBatch && onInlineSave && (
            <Button
              variant='ghost'
              size='icon'
              className='h-8 w-8'
              disabled={isSaving || endPage < startPage}
              onClick={() => onInlineSave(registration.id, startPage, endPage)}
              title={t('saveBatch')}
            >
              <Save className='h-4 w-4' />
            </Button>
          )}
          <Button
            variant='ghost'
            size='icon'
            className='h-8 w-8'
            disabled={!hasBatch}
            onClick={() => onViewQrData(registration)}
            title={t('viewQrData')}
          >
            <QrCode className='h-4 w-4' />
          </Button>
          <Button
            variant='ghost'
            size='icon'
            className='h-8 w-8'
            onClick={() =>
              router.push(
                `/dashboard/containers/${registration.id}?stage=sticking`
              )
            }
            title={t('viewContainer')}
          >
            <Eye className='h-4 w-4' />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
